import useSEO from '@/hooks/useSEO';
import { hiddenCostsFAQs } from './data';
import HiddenCostsGridSection from './components/HiddenCostsGridSection';
import ConversionCostSection from './components/ConversionCostSection';
import TraderProfileImpact from './components/TraderProfileImpact';
import BrokerTransparencyScorecard from './components/BrokerTransparencyScorecard';
import AccountAuditChecklist from './components/AccountAuditChecklist';
import HiddenCostsFAQSection from './components/HiddenCostsFAQSection';
import RelatedGuidesSection from './components/RelatedGuidesSection';

export default function HiddenForexTradingCosts() {
  useSEO({
    /* @replace:hidden-title */

    title: 'Hidden Forex Trading Costs: Fees Traders Often Miss',
    /* @replace:hidden-description */

    description: 'Identify currency conversion markups, withdrawal fees, inactivity charges and other hidden forex costs that can reduce your account returns.',
    /* @replace:hidden-keywords */

    keywords: 'hidden forex costs, currency conversion fee, withdrawal fee, inactivity fee, broker charges, indirect trading costs',
    /* @replace:hidden-canonical */

    canonical: '/hidden-forex-trading-costs/',
  });

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: hiddenCostsFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-foreground-950">
        {/* Decorative: fine dot grid */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--secondary-500)) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Decorative: large soft glow — "below the surface" metaphor */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[30%] w-[900px] h-[600px] rounded-full opacity-[0.06] blur-[160px] bg-accent-500" />
        <div className="absolute bottom-0 left-[20%] w-[500px] h-[400px] rounded-full opacity-[0.04] blur-[120px] bg-secondary-500" />

        {/* Decorative: concentric rings — depth layers */}
        <div className="absolute -top-[180px] -right-[120px] w-[560px] h-[560px] rounded-full border border-secondary-500/10" />
        <div className="absolute -top-[140px] -right-[80px] w-[480px] h-[480px] rounded-full border border-secondary-500/7" />
        <div className="absolute -top-[100px] -right-[40px] w-[400px] h-[400px] rounded-full border border-secondary-500/5" />

        {/* Decorative: offset second ring cluster — hidden beneath */}
        <div className="absolute -bottom-[140px] -left-[80px] w-[480px] h-[480px] rounded-full border border-accent-500/8" />
        <div className="absolute -bottom-[100px] -left-[40px] w-[360px] h-[360px] rounded-full border border-accent-500/5" />

        {/* Decorative: thin horizontal lines — surface tension motif */}
        <div className="absolute top-[50%] left-0 w-[180px] h-[1px] bg-gradient-to-r from-accent-500/0 via-accent-500/20 to-accent-500/0" />
        <div className="absolute top-[52%] right-0 w-[160px] h-[1px] bg-gradient-to-l from-secondary-500/0 via-secondary-500/16 to-secondary-500/0" />

        {/* Decorative: tiny crosses scattered */}
        <span className="absolute top-[28%] left-[12%] text-accent-500/10 text-[14px] font-light select-none">+</span>
        <span className="absolute top-[45%] right-[16%] text-secondary-500/8 text-[12px] font-light select-none">+</span>
        <span className="absolute bottom-[38%] left-[24%] text-accent-500/6 text-[10px] font-light select-none">+</span>

        {/* Decorative: vertical bars — depth markers */}
        <div className="absolute top-[15%] right-[6%] flex gap-3">
          <div className="w-[1px] h-16 bg-secondary-500/8" />
          <div className="w-[1px] h-10 bg-accent-500/10" />
          <div className="w-[1px] h-20 bg-secondary-500/6" />
        </div>

        {/* Background image: abstract dark depth / hidden layer concept */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20underwater%20depth%20layers%20with%20subtle%20iceberg%20silhouette%20below%20surface%20line%2C%20minimalist%20atmospheric%20art%2C%20deep%20shadows%20and%20faint%20warm%20light%20filtering%20from%20above%2C%20invisible%20layers%20metaphor%20for%20hidden%20fees%2C%20moody%20professional%20dark%20palette%20with%20soft%20amber%20and%20cool%20gray%20tones%2C%20abstract%20conceptual%20art%20no%20text&width=1600&height=700&seq=hidden-costs-hero-v2&orientation=landscape"
            alt="Hidden costs beneath the surface concept"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/40 to-foreground-950/80" />
        </div>

        <div className="relative max-w-main mx-auto px-4 md:px-6 pt-16 md:pt-24 pb-16 md:pb-20 text-center">
          <h1 className="text-[38px] md:text-[56px] font-[750] text-background-50 leading-[1.08] tracking-tight mb-5">
            The
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">hidden forex costs</span>
              <span className="absolute left-0 right-0 bottom-[2px] h-[5px] rounded-full bg-accent-500/60" />
            </span>
          </h1>
          <p className="text-[16px] md:text-[18px] text-foreground-400 leading-relaxed max-w-[620px] mx-auto mb-10">
            Costs that never appear on your trade ticket. Currency conversion markups, withdrawal fees, inactivity penalties — and five more draining your account silently.
          </p>

          <div className="max-w-[720px] mx-auto bg-background-100/10 border border-background-200/20 rounded-[24px] p-6 md:p-8 backdrop-blur-[10px]">
            <div className="text-[22px] md:text-[28px] font-bold text-background-50 leading-[1.3] text-center mb-8">
              A 0.4% conversion markup costs more than spread + commission combined
            </div>
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              <div className="text-center p-3 md:p-4 bg-background-100/8 rounded-xl">
                <div className="text-[32px] md:text-[40px] font-[750] text-accent-400 leading-none mb-1.5 tabular-nums">6</div>
                <div className="text-[12px] md:text-[13px] text-foreground-400 leading-tight">Hidden cost categories</div>
              </div>
              <div className="text-center p-3 md:p-4 bg-background-100/8 rounded-xl">
                <div className="text-[32px] md:text-[40px] font-[750] text-primary-400 leading-none mb-1.5 tabular-nums">$2K</div>
                <div className="text-[12px] md:text-[13px] text-foreground-400 leading-tight">Typical annual drain</div>
              </div>
              <div className="text-center p-3 md:p-4 bg-background-100/8 rounded-xl">
                <div className="text-[32px] md:text-[40px] font-[750] text-foreground-400 leading-none mb-1.5 tabular-nums">0</div>
                <div className="text-[12px] md:text-[13px] text-foreground-400 leading-tight">Shown on trade ticket</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HiddenCostsGridSection />
      <ConversionCostSection />
      <TraderProfileImpact />
      <BrokerTransparencyScorecard />
      <AccountAuditChecklist />
      <HiddenCostsFAQSection />
      <RelatedGuidesSection />
    </div>
  );
}